import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiClient';

export default function DeleteAccountDialog({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const balance = Number(user?.wallet_balance || 0);
  const canConfirm = confirmText.trim().toUpperCase() === 'DELETE' && !deleting;

  const handleOpenChange = (next) => {
    if (deleting) return;
    if (!next) setConfirmText('');
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    try {
      const response = await apiFetch('/api/auth/me', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'DELETE' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.deleted) {
        throw new Error(data.detail || 'Could not delete your account');
      }
      onOpenChange(false);
      await logout();
      toast.success('Your account has been deleted');
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Could not delete your account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Delete account
          </DialogTitle>
          <DialogDescription>
            This permanently deletes your SkipdQ account and cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
          <li>Your profile, saved trucks and sign-in details will be removed.</li>
          <li>
            Your wallet balance
            {balance > 0 ? ` of CA$ ${balance.toFixed(2)}` : ''} will be forfeited and is not
            refundable.
          </li>
          <li>Past order records are kept anonymously for vendor accounting.</li>
          <li>New-account offers and referral credits won&apos;t be available again on this phone number.</li>
        </ul>

        <div className="space-y-2">
          <label htmlFor="delete-confirm" className="text-sm font-medium">
            Type <span className="font-mono font-semibold">DELETE</span> to confirm
          </label>
          <Input
            id="delete-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="DELETE"
            data-testid="delete-account-confirm-input"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canConfirm}
            data-testid="delete-account-confirm-btn"
          >
            {deleting ? 'Deleting…' : 'Delete account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
